import { Check, Loader2, Printer, MapPin, QrCode } from 'lucide-react';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { useActivationProgress, usePlacementChecklist } from '@/hooks/activation';
import {
  ActivationStepState,
  PLACEMENT_SPOTS,
  PlacementSpotId,
} from '@/types/activation/activation-local';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';

interface PackActivationTrackerProps {
  pack: ActivationPackWithTarget;
  brand?: string;
  /** Jump to the print order card. */
  onGoToPrintOrder?: () => void;
}

const STATE_COPY: Record<ActivationStepState, string> = {
  todo: 'Not yet',
  in_progress: 'In progress',
  done: 'Done',
};

const StepShell = ({
  icon,
  title,
  state,
  brand,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  state: ActivationStepState;
  brand: string;
  children: React.ReactNode;
}) => (
  <div
    className="rounded-xl border bg-white p-4"
    style={{
      borderColor: state === 'done' ? `${brand}55` : '#E5EAF0',
      backgroundColor: state === 'done' ? `${brand}08` : undefined,
    }}
  >
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${brand}12`, color: brand }}
        >
          {icon}
        </div>
        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</div>
      </div>
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
        style={
          state === 'done'
            ? { backgroundColor: `${brand}15`, color: brand }
            : { backgroundColor: '#F1F5F9', color: '#64748B' }
        }
      >
        {STATE_COPY[state]}
      </span>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

/**
 * PRINTED → PLACED → SCANNED for this pack, above everything else.
 *
 * PRINTED reads the real print order status and SCANNED reads real QR scan
 * activity. PLACED is a deliberately client-side checklist: nobody can tell us
 * from a server whether a sticker is actually on a front door.
 */
export const PackActivationTracker = ({
  pack,
  brand = '#4E9CFF',
  onGoToPrintOrder,
}: PackActivationTrackerProps) => {
  const progress = useActivationProgress(pack.id, pack.agent_id);
  const { checklist, toggle } = usePlacementChecklist(pack.id);

  const headline = progress.isLoading
    ? 'Checking how your activation is going…'
    : progress.scansThisWeek > 0
      ? `${progress.scansThisWeek} ${progress.scansThisWeek === 1 ? 'person' : 'people'} scanned your code this week`
      : progress.scansTotal > 0
        ? 'Nobody scanned your code this week'
        : 'Nobody has scanned your code yet';

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
      <div className="rounded-[14px] bg-white px-5 md:px-8 py-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">{headline}</h2>
          {!progress.isLoading && progress.scansTotal > 0 && (
            <span className="text-sm text-gray-500">{progress.scansTotal} scans all time</span>
          )}
        </div>

        {progress.printedButNeverScanned && (
          <p className="mt-2 text-sm text-gray-600">
            Your order went to the printer but nothing has been scanned yet — the most likely reason is that
            the printed pieces aren't up anywhere yet. Tick off the placements below as you put them out.
          </p>
        )}

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <StepShell
            icon={<Printer className="h-4 w-4" />}
            title="Printed"
            state={progress.printed}
            brand={brand}
          >
            {progress.printed === 'done' ? (
              <p className="text-sm text-gray-700">
                Sent to <CopyableEmail email={progress.printedVendorEmail} className="font-bold" />
                {progress.printedAt && ` on ${new Date(progress.printedAt).toLocaleDateString()}`}.
              </p>
            ) : progress.printed === 'in_progress' ? (
              <p className="flex items-center gap-2 text-sm text-gray-700">
                <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: brand }} />
                Your order is on its way to the printer.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Nothing sent to a printer yet.{' '}
                {onGoToPrintOrder && (
                  <button
                    type="button"
                    onClick={onGoToPrintOrder}
                    className="font-semibold hover:underline"
                    style={{ color: brand }}
                  >
                    Order your pack
                  </button>
                )}
              </p>
            )}
          </StepShell>

          <StepShell
            icon={<MapPin className="h-4 w-4" />}
            title="Placed"
            state={progress.placed}
            brand={brand}
          >
            <p className="mb-2 text-sm text-gray-600">
              {progress.placedCount} of {progress.placedTotal} spots ticked off — tap one when it's up.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {PLACEMENT_SPOTS.map((spot) => {
                const isChecked = !!checklist[spot.id as PlacementSpotId];
                return (
                  <button
                    key={spot.id}
                    type="button"
                    onClick={() => toggle(spot.id)}
                    aria-pressed={isChecked}
                    title={spot.hint}
                    className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors"
                    style={
                      isChecked
                        ? { borderColor: brand, color: brand, backgroundColor: `${brand}12` }
                        : { borderColor: '#E5EAF0', color: '#64748B' }
                    }
                  >
                    {isChecked ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <span className="h-3 w-3 rounded-full border border-current opacity-50" />
                    )}
                    {spot.label}
                  </button>
                );
              })}
            </div>
          </StepShell>

          <StepShell
            icon={<QrCode className="h-4 w-4" />}
            title="Scanned"
            state={progress.scanned}
            brand={brand}
          >
            {!pack.agent_id ? (
              <p className="text-sm text-gray-600">
                Scan tracking is available for packs that belong to a Genie.
              </p>
            ) : progress.isLoading ? (
              <p className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: brand }} />
                Loading your scans…
              </p>
            ) : progress.scansTotal === 0 ? (
              <p className="text-sm text-gray-600">
                No scans recorded yet. They show up here the moment someone points a phone at your code.
              </p>
            ) : (
              <p className="text-sm text-gray-700">
                <strong>{progress.scansThisWeek}</strong> this week ·{' '}
                <strong>{progress.scansTotal}</strong> all time.
              </p>
            )}
          </StepShell>
        </div>
      </div>
    </div>
  );
};
